import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ListChecksIcon as ListTodo,
  CalendarIcon as CalendarDays,
  LockIcon as Lock,
  QrCodeIcon as QrCode,
  ChatCircleIcon as MessageCircle,
  ShareNetworkIcon as Share2,
  ImageIcon,
  GearIcon as SettingsIcon,
  PuzzlePieceIcon as Puzzle,
  SquaresFourIcon as LayoutGrid,
  FeatherIcon as Feather,
  KeyIcon as KeySquare,
  ArrowUpRightIcon as ArrowUpRight,
  ListIcon as Menu,
  XIcon as X,
  FileTextIcon as FileText,
  MusicNoteIcon as Music,
  TerminalWindowIcon as Terminal,
  ClockIcon as Clock,
  HardDrivesIcon as HardDrive,
  CalculatorIcon as Calculator,
  DotsSixIcon as GripHorizontal,
  YoutubeLogoIcon,
  InstagramLogoIcon,
  LinkedinLogoIcon,
  DiscordLogoIcon,
  GithubLogoIcon,
  PackageIcon,
  XLogoIcon,
} from "@phosphor-icons/react";
import OS_DATA from "./data.json";
import "./App.css";

const ICON_MAP = {
  ListTodo,
  CalendarDays,
  Lock,
  QrCode,
  MessageCircle,
  Share2,
  ImageIcon,
  SettingsIcon,
  Puzzle,
  LayoutGrid,
  Feather,
  KeySquare,
  FileText,
  Music,
  Terminal,
  Clock,
  HardDrive,
  Calculator,
};

const ACCENTS = {
  violet: "#9D7BFF",
  cyan: "#4FD8C4",
  pink: "#FF6FB0",
};

const WAITLIST_EMAIL = "ssovee.org@zohomail.in";

const socialLinks = [
  {
    name: "GitHub",
    handle: "ssovee",
    description: "Follow the open-source build and product experiments.",
    href: "https://github.com/ssovee",
    icon: GithubLogoIcon,
    accent: "violet",
  },
  {
    name: "YouTube",
    handle: "@ssovee",
    description: "Watch product walkthroughs, demos, and launch notes.",
    href: "https://www.youtube.com/@ssovee",
    icon: YoutubeLogoIcon,
    accent: "pink",
  },
  {
    name: "X",
    handle: "@ssoveeos",
    description: "Catch quick updates, ideas, and conversations in motion.",
    href: "https://x.com/ssoveeos",
    icon: XLogoIcon,
    accent: "cyan",
  },
  {
    name: "Instagram",
    handle: "@ssovee.os",
    description: "See the visual side of SSOVEE OS as it takes shape.",
    href: "https://www.instagram.com/ssovee.os",
    icon: InstagramLogoIcon,
    accent: "pink",
  },
  {
    name: "LinkedIn",
    handle: "SSOVEE",
    description: "Keep up with company news, milestones, and announcements.",
    href: "https://www.linkedin.com/company/ssovee",
    icon: LinkedinLogoIcon,
    accent: "violet",
  },
  {
    name: "Discord",
    handle: "SSOVEE community",
    description: "Join the community and help shape what comes next.",
    href: "https://discord.com/invite/h7BX3rZfnB",
    icon: DiscordLogoIcon,
    accent: "cyan",
  },
  {
    name: "npm",
    handle: "ssovee-os-web-ui",
    description: "Explore the package behind the SSOVEE web experience.",
    href: "https://www.npmjs.com/package/ssovee-os-web-ui",
    icon: PackageIcon,
    accent: "violet",
  },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------------------------------- Starfield ---------------------------------- */
function Starfield() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    const isMobile = window.innerWidth < 768;
    const count = isMobile ? 400 : 1200;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const palette = [
      [0.616, 0.482, 1],
      [1, 0.435, 0.69],
      [0.31, 0.847, 0.769],
    ];

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 16;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 16;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 16;
      const c = palette[Math.floor(Math.random() * palette.length)];
      colors[i * 3] = c[0];
      colors[i * 3 + 1] = c[1];
      colors[i * 3 + 2] = c[2];
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      size: 0.045,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
    });
    const points = new THREE.Points(geometry, material);
    scene.add(points);

    let targetX = 0;
    let targetY = 0;
    const handlePointer = (e) => {
      targetX = (e.clientX / window.innerWidth - 0.5) * 0.5;
      targetY = (e.clientY / window.innerHeight - 0.5) * 0.5;
    };
    window.addEventListener("pointermove", handlePointer);

    let frameId;
    const animate = () => {
      points.rotation.y += 0.0006;
      points.rotation.x += (targetY - points.rotation.x) * 0.01 + 0.0001;
      points.rotation.y += (targetX - points.rotation.y) * 0.002;
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", handlePointer);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <canvas ref={canvasRef} className="ssv-starfield" aria-hidden="true" />
  );
}

/* ---------------------------------- Tilt Wrapper ---------------------------------- */
function TiltCard({ children, className = "", maxTilt = 7, style = {} }) {
  const ref = useRef(null);
  const [transform, setTransform] = useState(
    "perspective(900px) rotateX(0deg) rotateY(0deg)",
  );
  const [transition, setTransition] = useState(
    "transform 400ms cubic-bezier(0.2,0.8,0.2,1)",
  );

  const handleMove = useCallback(
    (e) => {
      if (prefersReducedMotion() || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      setTransition("transform 60ms linear");
      setTransform(
        `perspective(900px) rotateX(${(-py * maxTilt).toFixed(
          2,
        )}deg) rotateY(${(px * maxTilt).toFixed(2)}deg) translateZ(0)`,
      );
    },
    [maxTilt],
  );

  const handleLeave = useCallback(() => {
    setTransition("transform 500ms cubic-bezier(0.2,0.8,0.2,1)");
    setTransform("perspective(900px) rotateX(0deg) rotateY(0deg)");
  }, []);

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      style={{ ...style, transform, transition }}
    >
      {children}
    </div>
  );
}

/* ---------------------------------- macOS Dock ---------------------------------- */
function Dock({ apps }) {
  const itemRefs = useRef([]);
  const [scales, setScales] = useState(apps.map(() => 1));

  const handleMove = (e) => {
    if (prefersReducedMotion()) return;
    const next = itemRefs.current.map((el) => {
      if (!el) return 1;
      const rect = el.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const dist = Math.abs(e.clientX - center);
      const influence = Math.max(0, 1 - dist / 110);
      return 1 + influence * 0.55;
    });
    setScales(next);
  };

  const handleLeave = () => setScales(apps.map(() => 1));

  return (
    <nav
      aria-label="Featured apps dock"
      className="ssv-dock"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      {apps.map((app, i) => {
        const Icon = ICON_MAP[app.icon];
        const scale = scales[i] || 1;
        const zIndexVal = Math.round(scale * 10) + 100;

        return (
          <a
            key={app.id}
            href="#apps"
            ref={(el) => (itemRefs.current[i] = el)}
            className="ssv-dock-item"
            data-accent={app.accent}
            style={{
              transform: `scale(${scale}) translateY(${(scale - 1) * -16}px)`,
              transition: "transform 120ms cubic-bezier(0.2, 0, 0, 1)",
              zIndex: zIndexVal,
            }}
          >
            {Icon && <Icon size={22} weight="regular" />}
            <span className="ssv-dock-label">{app.name}</span>
          </a>
        );
      })}
    </nav>
  );
}

/* ---------------------------------- Live Clock ---------------------------------- */
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const dateStr = now.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const timeStr = now.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <span className="ssv-clock">
      <span>{dateStr}</span>
      <span className="ssv-clock-divider" aria-hidden="true" />
      <span>{timeStr}</span>
    </span>
  );
}

/* ---------------------------------- App Card ---------------------------------- */
function AppCard({ app }) {
  const Icon = ICON_MAP[app.icon];
  return (
    <TiltCard
      className="ssv-app-card"
      maxTilt={5}
      style={{ "--accent": ACCENTS[app.accent] }}
    >
      <div className="ssv-app-icon" data-accent={app.accent}>
        {Icon && <Icon size={20} weight="regular" />}
      </div>
      <p className="ssv-app-category">{app.category}</p>
      <h3 className="ssv-app-name">{app.name}</h3>
      <p className="ssv-app-tagline">{app.tagline}</p>
      <p className="ssv-app-desc">{app.description}</p>
    </TiltCard>
  );
}

/* ---------------------------------- Draggable Hero Window Card ---------------------------------- */
function HeroWindow({ app, style, big, dragBoundsRef }) {
  const Icon = ICON_MAP[app.icon];
  return (
    <motion.div
      drag
      dragConstraints={dragBoundsRef}
      dragElastic={0.1}
      whileDrag={{ scale: 1.05, cursor: "grabbing" }}
      className={`ssv-hero-window ${big ? "ssv-hero-window--lg" : ""}`}
      style={{ ...style, "--accent": ACCENTS[app.accent] }}
    >
      <div className="ssv-hero-window-bar">
        <span className="ssv-dot ssv-dot--r" />
        <span className="ssv-dot ssv-dot--y" />
        <span className="ssv-dot ssv-dot--g" />
        <span className="ssv-hero-window-title">{app.name}</span>
        <GripHorizontal
          size={12}
          weight="regular"
          className="ssv-window-drag-icon"
        />
      </div>
      <div className="ssv-hero-window-body">
        <div className="ssv-app-icon" data-accent={app.accent}>
          {Icon && <Icon size={18} weight="regular" />}
        </div>
        <p className="ssv-hero-window-tagline">{app.tagline}</p>
      </div>
    </motion.div>
  );
}

/* ---------------------------------- Main App Component ---------------------------------- */
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const stageBoundsRef = useRef(null);
  const { meta, apps, pillars, stats } = OS_DATA;
  const heroApps = [apps[0], apps[1], apps[2], apps[4]];

  const handleWaitlistSubmit = (e) => {
    e.preventDefault();
    const enteredEmail = e.target.elements["ssv-email"]?.value || "";
    const subject = encodeURIComponent(`${meta.name} — Access request`);
    const body = encodeURIComponent(
      `Hi SSOVEE team,\n\nI'd like early access to ${meta.name}.\n\nEmail: ${enteredEmail}\n\nThanks!`,
    );
    window.location.href = `mailto:${WAITLIST_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="ssv-root">
      <Starfield />

      {/* Top Navigation */}
      <header className="ssv-topbar">
        <div className="ssv-logo">
          <span className="ssv-logo-mark" aria-hidden="true" />
          <span>{meta.name}</span>
        </div>
        <nav className="ssv-nav" aria-label="Primary">
          <a href="#apps">Apps</a>
          <a href="#vision">Vision</a>
          <a href="#social">Community</a>
          <a href="#join">Get started</a>
        </nav>
        <div className="ssv-topbar-right">
          <LiveClock />
          <button
            className="ssv-cta-small"
            type="button"
            onClick={() => navigate("/playground")}
          >
            {meta.actionCta}
          </button>
          <button
            className="ssv-menu-btn"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? (
              <X size={22} weight="regular" />
            ) : (
              <Menu size={22} weight="regular" />
            )}
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="ssv-mobile-menu">
          <a href="#apps" onClick={() => setMenuOpen(false)}>
            Apps
          </a>
          <a href="#vision" onClick={() => setMenuOpen(false)}>
            Vision
          </a>
          <a href="#social" onClick={() => setMenuOpen(false)}>
            Community
          </a>
          <a href="#join" onClick={() => setMenuOpen(false)}>
            Get started
          </a>
        </div>
      )}

      {/* Hero Section (Stationary Container) */}
      <main>
        <section className="ssv-hero" aria-label="Introduction">
          <div className="ssv-hero-copy">
            <div className="ssv-eyebrow-row">
              <span className="ssv-version-pill">
                {meta.version} · Lucid OS
              </span>
            </div>
            <h1>{meta.tagline}</h1>
            <p className="ssv-hero-sub">{meta.subheading}</p>
            <div className="ssv-hero-actions">
              <button
                className="ssv-btn-primary"
                type="button"
                onClick={() => navigate("/playground")}
              >
                {meta.actionCta}
              </button>
              <a href="#apps" className="ssv-btn-secondary">
                {meta.secondaryCta} <ArrowUpRight size={16} weight="regular" />
              </a>
            </div>
          </div>

          {/* Desktop Draggable Window Stage */}
          <div className="ssv-stage" ref={stageBoundsRef}>
            <HeroWindow
              app={heroApps[0]}
              big
              style={{ top: "4%", left: "4%" }}
              dragBoundsRef={stageBoundsRef}
            />
            <HeroWindow
              app={heroApps[1]}
              style={{ top: "0%", right: "2%" }}
              dragBoundsRef={stageBoundsRef}
            />
            <HeroWindow
              app={heroApps[2]}
              style={{ bottom: "4%", left: "14%" }}
              dragBoundsRef={stageBoundsRef}
            />
            <HeroWindow
              app={heroApps[3]}
              style={{ bottom: "0%", right: "8%" }}
              dragBoundsRef={stageBoundsRef}
            />
          </div>
        </section>

        {/* MacBook macOS Dock Section */}
        <div className="ssv-dock-wrapper">
          <Dock apps={apps} />
        </div>

        {/* Apps Grid Section */}
        <section className="ssv-section" id="apps">
          <div className="ssv-section-head">
            <h2>Everything you open before breakfast, in one OS</h2>
            <p>
              15 core apps ship with SSOVEE OS by default. Each one opens
              instantly, remembers where you left off, and shares the same
              identity.
            </p>
          </div>
          <div className="ssv-app-grid">
            {apps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        </section>

        {/* Vision Section */}
        <section className="ssv-section" id="vision">
          <div className="ssv-section-head">
            <h2>Built around three core ideas</h2>
            <p>
              SSOVEE OS isn't trying to replace your desktop — it's replacing
              the endless browser tabs you keep pinned open daily.
            </p>
          </div>
          <div className="ssv-pillars">
            {pillars.map((pillar) => {
              const Icon = ICON_MAP[pillar.icon];
              return (
                <div className="ssv-pillar" key={pillar.title}>
                  <div className="ssv-pillar-icon">
                    {Icon && <Icon size={18} weight="regular" />}
                  </div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.description}</p>
                </div>
              );
            })}
          </div>
          <div className="ssv-stats">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="ssv-stat-value">{s.value}</div>
                <div className="ssv-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Social Community Section */}
        <section className="ssv-social-section" id="social">
          <div className="ssv-section-head">
            <span className="ssv-section-kicker">The signal layer</span>
            <h2>Follow the build beyond the browser</h2>
            <p>
              Product drops, behind-the-scenes notes, and the conversations
              shaping SSOVEE OS happen across the community.
            </p>
          </div>
          <div className="ssv-social-grid">
            {socialLinks.map(
              ({ name, handle, description, href, icon: Icon, accent }) => (
                <a
                  className="ssv-social-card"
                  data-accent={accent}
                  href={href}
                  key={name}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="ssv-social-card-icon">
                    <Icon size={22} weight="regular" />
                  </span>
                  <span className="ssv-social-card-topline">
                    <span className="ssv-social-card-name">{name}</span>
                    <ArrowUpRight
                      size={16}
                      weight="regular"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="ssv-social-card-handle">{handle}</span>
                  <span className="ssv-social-card-description">
                    {description}
                  </span>
                </a>
              ),
            )}
          </div>
        </section>

        {/* Join Waitlist Panel */}
        <section className="ssv-cta-panel" id="join">
          <div>
            <h2>Get SSOVEE OS when it opens up</h2>
            <p>
              Drop your email address below to join the early access queue. No
              spam, just your workspace invite key.
            </p>
          </div>
          <form
            className="ssv-cta-form"
            onSubmit={handleWaitlistSubmit}
            aria-label="Join the waitlist"
          >
            <label htmlFor="ssv-email" className="ssv-sr-only">
              Email address
            </label>
            <input
              id="ssv-email"
              name="ssv-email"
              type="email"
              required
              placeholder="you@example.com"
            />
            <button className="ssv-btn-primary" type="submit">
              {meta.primaryCta}
            </button>
          </form>
        </section>
      </main>

      <footer className="ssv-footer">
        <span>
          © {new Date().getFullYear()} {meta.name}. Cloud-native, private,
          yours.
        </span>
        <div className="ssv-footer-links">
          <a href="#apps">Apps</a>
          <a href="#vision">Vision</a>
          <a href="#social">Community</a>
          <a href="#join">Get started</a>
        </div>
        <div className="ssv-footer-social">
          {socialLinks.map(({ name, href, icon: Icon }) => (
            <a
              href={href}
              key={name}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`SSOVEE on ${name}`}
            >
              <Icon size={18} weight="regular" />
            </a>
          ))}
        </div>
      </footer>
    </div>
  );
}
